import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Dumbbell,
  Calendar,
  BarChart3,
  Users,
  LogOut,
  Menu,
  X,
  Trophy,
  Timer,
  Shield,
  CircleHelp,
  UserRound,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore, getAppText } from '../../stores/languageStore';
import { useUserData } from '../../hooks/useUserData';
import { getActiveRole, getRoleLabels, normalizeRoles } from '../../utils/roles';
import { triggerCrazyMenuBurst } from '../../utils/crazyBurst';

export const Sidebar = ({ onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const { user } = useAuthStore();
  const { appLanguage } = useLanguageStore();
  const { userData } = useUserData();
  const resolvedUserData = userData || user;
  const t = getAppText(appLanguage);
  const roleLabels = getRoleLabels(resolvedUserData);
  const activeRole = getActiveRole(resolvedUserData);
  const availableRoles = normalizeRoles(resolvedUserData);
  const canOpenPlayerDashboard = availableRoles.includes('player');
  const canOpenTrainerDashboard = availableRoles.includes('trainer') || availableRoles.includes('admin');
  const canOpenAdminDashboard = availableRoles.includes('admin');
  const roleContextFromPath = location.pathname.startsWith('/player')
    ? 'player'
    : location.pathname.startsWith('/coach')
      ? 'trainer'
      : location.pathname.startsWith('/admin')
        ? 'admin'
        : activeRole;

  const rolePanelItems = [
    ...(roleContextFromPath === 'player' && canOpenPlayerDashboard
      ? [{ icon: UserRound, label: t.playerPanel, href: '/player', color: 'text-secondary-400' }]
      : []),
    ...(roleContextFromPath === 'trainer' && canOpenTrainerDashboard
      ? [{ icon: Users, label: t.trainerPanel, href: '/coach', color: 'text-secondary-500' }]
      : []),
    ...(roleContextFromPath === 'admin' && canOpenAdminDashboard
      ? [{ icon: Shield, label: t.adminPanel, href: '/admin', color: 'text-primary-600' }]
      : []),
  ];

  const baseMenuItems = [
    { icon: Home, label: t.dashboard, href: '/', color: 'text-primary-500' },
    { icon: Dumbbell, label: t.activities, href: '/activities', color: 'text-primary-500' },
    { icon: Timer, label: t.training, href: '/training', color: 'text-accent-500' },
    { icon: Calendar, label: t.calendar, href: '/calendar', color: 'text-secondary-500' },
    { icon: BarChart3, label: t.stats, href: '/stats', color: 'text-secondary-500' },
    { icon: Trophy, label: t.challenges, href: '/challenges', color: 'text-accent-500' },
    { icon: Users, label: t.groups, href: '/groups', color: 'text-primary-500' },
  ];

  const menuSections = [
    {
      label: t.basics,
      items: baseMenuItems.slice(0, 4),
    },
    {
      label: t.development,
      items: [
        ...baseMenuItems.slice(4),
      ],
    },
    {
      label: t.management,
      items: rolePanelItems,
    },
    {
      label: t.helpAndFormality,
      items: [
        { icon: CircleHelp, label: t.help, href: '/help', color: 'text-secondary-400' },
        { icon: ShieldCheck, label: t.privacy, href: '/privacy-terms', color: 'text-accent-500' },
      ],
    },
  ].filter((section) => section.items.length > 0);

  const menuItems = menuSections.flatMap((section) => section.items);

  useEffect(() => {
    const root = document.documentElement;
    const mobileQuery = window.matchMedia('(max-width: 767.98px)');

    const syncMobileSidebarState = () => {
      root.classList.toggle('sidebar-mobile-open', isOpen && mobileQuery.matches);
    };

    syncMobileSidebarState();
    mobileQuery.addEventListener?.('change', syncMobileSidebarState);

    return () => {
      mobileQuery.removeEventListener?.('change', syncMobileSidebarState);
      root.classList.remove('sidebar-mobile-open');
    };
  }, [isOpen]);

  const isActive = (href) => {
    if (href === '/activities') {
      return ['/activities', '/exercises', '/workouts'].includes(location.pathname);
    }

    if (href === '/training') {
      return ['/training', '/timer'].includes(location.pathname);
    }

    return location.pathname === href;
  };

  const handleMenuClick = (event) => {
    triggerCrazyMenuBurst(event.currentTarget);
    setIsOpen(false);
  };

  return (
    <>
      {isOpen ? (
        <button
          type="button"
          aria-label="Zamknij menu"
          className="fixed inset-0 z-30 bg-neutral-950/45 backdrop-blur-[1px] md:hidden"
          onClick={() => setIsOpen(false)}
        />
      ) : null}
      <button
        className="app-sidebar-handle md:hidden fixed left-0 top-[5.6rem] z-50 h-14 w-9 -translate-x-[2px] rounded-r-2xl border border-white/35 bg-gradient-to-b from-primary-600 to-secondary-600 text-white shadow-[0_10px_24px_rgba(38,77,125,0.38)] flex items-center justify-center"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Zamknij menu' : 'Otwórz menu'}
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      <aside
        className={`${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } app-sidebar md:translate-x-0 fixed md:static left-0 top-0 z-40 w-72 h-screen bg-white/70 dark:bg-neutral-950/80 backdrop-blur-2xl text-neutral-900 dark:text-white transition-transform duration-300 flex flex-col border-r border-white/65 dark:border-neutral-800 shadow-[0_18px_36px_-24px_rgba(15,23,42,0.5)]`}
        aria-label="Główne menu nawigacyjne"
      >
        {/* Logo Section */}
        <div className="app-sidebar-brand p-6 bg-gradient-to-br from-secondary-700 via-primary-700 to-primary-600 dark:from-secondary-900 dark:via-primary-900 dark:to-primary-800 border-b border-white/20">
          <div className="flex items-center justify-center mb-3">
            <div className="h-20 w-20 rounded-2xl overflow-hidden bg-white/15 backdrop-blur-sm p-2 flex items-center justify-center border border-white/20 shadow-[0_10px_24px_-12px_rgba(0,0,0,0.45)]">
              {!logoError ? (
                <img
                  src="/pkk-logo.png"
                  alt="PKK 99"
                  className="h-full w-full object-contain"
                  onError={() => setLogoError(true)}
                  loading="lazy"
                />
              ) : (
                <span className="text-2xl font-black">PKK</span>
              )}
            </div>
          </div>
          <p className="text-primary-100 text-[11px] font-semibold tracking-[0.18em] text-center">SPORT OS / CONTROL HUB</p>
          <div className="mt-3 space-y-1">
            <p className="text-primary-100 text-xs truncate text-center">
              {user?.displayName || 'Użytkownik'}
            </p>
            {roleLabels.length > 0 && (
              <p className="text-white text-xs font-semibold text-center">
                {roleLabels.join(' • ')}
              </p>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto genie-dock">
          {menuSections.map((section) => (
            <div key={section.label} className="space-y-2">
              <p className="px-2 text-[10px] font-black uppercase tracking-[0.24em] text-neutral-500 dark:text-neutral-400">
                {section.label}
              </p>
              <div className="space-y-1.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={handleMenuClick}
                      style={{ animationDelay: `${menuItems.indexOf(item) * 40}ms` }}
                      className={`app-sidebar-link genie-dock-item flex items-center gap-3 px-3.5 py-3 rounded-2xl transition-all duration-200 group min-h-[48px] ${
                        active
                          ? 'app-sidebar-link-active bg-gradient-to-r from-primary-100 to-secondary-100 dark:from-primary-900/35 dark:to-secondary-900/25 text-primary-900 dark:text-primary-200 shadow-[0_8px_20px_-14px_rgba(63,93,136,0.55)] ring-1 ring-primary-300/60 dark:ring-primary-700/60'
                          : 'text-neutral-700 dark:text-neutral-300 hover:bg-white/70 dark:hover:bg-neutral-800/80'
                      } animate-slide-in-left`}
                    >
                      <span className={`inline-flex h-8 w-8 items-center justify-center rounded-xl border ${active ? 'border-primary-200 dark:border-primary-700 bg-white/70 dark:bg-neutral-900/40' : 'border-neutral-200/80 dark:border-neutral-700 bg-white/75 dark:bg-neutral-900/40'}`}>
                        <Icon className={`w-4 h-4 ${active ? 'text-primary-700 dark:text-primary-200' : 'text-neutral-500 group-hover:' + item.color}`} />
                      </span>
                      <span className={`font-medium ${active ? 'text-primary-900 dark:text-primary-200' : 'text-neutral-700 dark:text-neutral-300'}`}>
                        {item.label}
                      </span>
                      {active && <div className="ml-auto w-2 h-2 rounded-full bg-primary-600 dark:bg-primary-300"></div>}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-neutral-200/70 dark:border-neutral-800">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl min-h-[48px] text-neutral-700 dark:text-neutral-300 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300 transition-all duration-200 group"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white/80 dark:bg-neutral-900/50 group-hover:border-red-300 dark:group-hover:border-red-700">
              <LogOut className="w-4 h-4" />
            </span>
            <span className="font-medium">{t.logout}</span>
            <Zap className="ml-auto w-4 h-4 opacity-50" />
          </button>
        </div>
      </aside>
    </>
  );
};
